import { getUiLanguage, getUiLocale } from './uiLocale.js';

// Until the store's settings load: platform currency, and the dashboard language for formatting.
const DEFAULT_LOCALE = null;
const DEFAULT_CURRENCY = 'USD';

const ALLOWED_CURRENCIES = new Set([
    'TRY',
    'USD',
    'EUR',
    'GBP',
    'AED',
    'SAR',
    'JPY',
    'CAD',
    'AUD',
    'AFN',
    'CHF',
    'BRL',
    'INR',
    'KWD',
    'BHD',
    'QAR',
    'OMR',
    'EGP',
    'JOD',
]);

const CURRENCY_SYMBOLS = {
    TRY: '\u20BA',
    USD: '$',
    EUR: '\u20AC',
    GBP: '\u00A3',
    AED: 'AED',
    SAR: 'SAR',
    JPY: '\u00A5',
    CAD: 'C$',
    AUD: 'A$',
    AFN: 'AFN',
    CHF: 'CHF',
    BRL: 'R$',
    INR: '\u20B9',
    KWD: 'KWD',
    BHD: 'BHD',
    QAR: 'QAR',
    OMR: 'OMR',
    EGP: 'EGP',
    JOD: 'JOD',
};

export const APP_CURRENCY_CONFIG = {
    locale: DEFAULT_LOCALE,
    currency: DEFAULT_CURRENCY,
};

const normalizeLocale = (locale) => {
    if (typeof locale !== 'string') return null;
    const trimmedLocale = locale.trim();
    return trimmedLocale || null;
};

const normalizeCurrency = (currency) => {
    if (typeof currency !== 'string') return null;
    const normalizedCurrency = currency.trim().toUpperCase();
    return ALLOWED_CURRENCIES.has(normalizedCurrency) ? normalizedCurrency : null;
};

export const setCurrencyConfig = ({ locale, currency } = {}) => {
    const nextLocale = normalizeLocale(locale);
    const nextCurrency = normalizeCurrency(currency);

    if (nextLocale) APP_CURRENCY_CONFIG.locale = nextLocale;
    if (nextCurrency) APP_CURRENCY_CONFIG.currency = nextCurrency;
};

export const getCurrencyConfig = () => ({ ...APP_CURRENCY_CONFIG });

// Symbols shown instead of the code when the dashboard is in Arabic or Persian.
const RTL_CURRENCY_SYMBOLS = {
    SAR: 'ر.س',
};

export const getCurrencySymbol = (currencyCode = APP_CURRENCY_CONFIG.currency) => {
    if (['ar', 'fa'].includes(getUiLanguage()) && RTL_CURRENCY_SYMBOLS[currencyCode]) {
        return RTL_CURRENCY_SYMBOLS[currencyCode];
    }
    return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
};

// Arabic / Persian store locales (e.g. ar-SA) keep Latin digits, like the rest of the dashboard.
const withLatinDigits = (locale) =>
    /^(ar|fa)\b/i.test(locale) && !/-u-/i.test(locale) ? `${locale}-u-nu-latn` : locale;

export const getCurrencyAxisFormat = (currencyCode = APP_CURRENCY_CONFIG.currency) => {
    return `${getCurrencySymbol(currencyCode)}#,###`;
};

export const formatCurrency = (amount, options = {}) => {
    const locale = withLatinDigits(options.locale || APP_CURRENCY_CONFIG.locale || getUiLocale());
    const currency = options.currency || APP_CURRENCY_CONFIG.currency;
    const numericAmount = Number(amount);
    const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0;

    const formatterOptions = {
        style: 'currency',
        currency,
    };

    if (options.minimumFractionDigits !== undefined) {
        formatterOptions.minimumFractionDigits = options.minimumFractionDigits;
    }

    if (options.maximumFractionDigits !== undefined) {
        formatterOptions.maximumFractionDigits = options.maximumFractionDigits;
    }

    return new Intl.NumberFormat(locale, formatterOptions).format(safeAmount);
};

export const formatPrice = formatCurrency;
